// Strict markdown stripper for user-facing surfaces. Drops every common
// markdown construct (headings, bold/italic, blockquotes, list bullets and
// numbering, inline code, code blocks, links, images, horizontal rules) and
// collapses excess whitespace, returning a clean plain-text rendering safe
// for app-card descriptions, list previews, etc.
//
// The complement of `stripMarkdownLead` in summarize-prompt.ts: that one
// only strips the LEADING markdown noise (so the first sentence reads
// cleanly); this one nukes markdown throughout the string so a workspace
// card never shows raw `## 2. **Split Bill**...` again.

export function stripMarkdown(input: string): string {
  if (!input) return '';
  return input
    // Fenced code blocks FIRST so the inner backticks aren't double-eaten.
    .replace(/```[\s\S]*?```/g, '')
    // Inline code.
    .replace(/`([^`]+)`/g, '$1')
    // Images: ![alt](url) → alt
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    // Links: [text](url) → text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Headings: leading #'s on a line.
    .replace(/^\s*#{1,6}\s+/gm, '')
    // Blockquotes: leading >'s on a line.
    .replace(/^\s*>+\s?/gm, '')
    // Unordered-list markers.
    .replace(/^\s*[-*+]\s+/gm, '')
    // Ordered-list markers ("1. ", "23. ", …).
    .replace(/^\s*\d+\.\s+/gm, '')
    // Bold — **text** / __text__ → text. Run before italic so we don't eat **.
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    // Italic — *text* / _text_ → text. Lookaround keeps unrelated * / _ alone.
    .replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '$1')
    .replace(/(?<!_)_([^_\n]+)_(?!_)/g, '$1')
    // Horizontal rules.
    .replace(/^\s*[-*_]{3,}\s*$/gm, '')
    // Collapse 3+ newlines into 2.
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
